// The snippets' `onScrollOnce` + `_.debounce(trackCandidateProfileScroll, 2000)`
// (`extension-alert-candidate-snippet.component.ng1.ts:151`, `:272-299`; the contact snippet's
// `:405-431` is the same code).
//
// ⚠️ ONCE PER SNIPPET, TWO SECONDS AFTER THE FIRST SCROLL, MEASURED THEN. `onScrollOnce` hands
// the FIRST scroll event to the debounced function and sets `hasScrolled`, so the debounce is
// called exactly once and fires 2000ms later — reading `scrollTop` off the element at THAT moment,
// not at the first event. Later scrolls on the same snippet record nothing. A new snippet (another
// candidate) starts again; opening More does not, because More is a child state of the same
// snippet.
//
// ⚠️ AND THE TIMER OUTLIVES THE SCREEN. A lodash debounce is not cancelled when the component is
// destroyed, so closing the profile inside the two seconds still sends the event, measured off
// the detached element. Clearing the timer on unmount would be tidier and would drop those rows.
import { useCallback, useEffect, useRef } from 'react'
import type { ProfileScrollMetrics } from './actionTracking.ts'

const SCROLL_TRACK_DELAY_MS = 2000

/**
 * jQuery's `.height()` — the CONTENT height, so a `border-box` element loses its padding and
 * border, read from the computed style as jQuery does.
 */
function jqueryHeight(element: HTMLElement): number {
  const style = getComputedStyle(element)
  const height = Number.parseFloat(style.height)
  if (Number.isNaN(height)) return element.clientHeight
  if (style.boxSizing !== 'border-box') return height
  const box = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth'] as const
  return box.reduce((rest, key) => rest - (Number.parseFloat(style[key]) || 0), height)
}

function measure(element: HTMLElement): ProfileScrollMetrics {
  const scrollTop = element.scrollTop
  const scrollHeight = element.scrollHeight - jqueryHeight(element)
  return { scrollTop, scrollHeight, scrollDepthPercentage: Math.round((scrollTop / scrollHeight) * 100) }
}

/**
 * The snippet's `onContentScroll` for `entity`. `track` is called with the entity as it is when
 * the timer fires (`this.candidateAlertInfo` is read then too), and `resetKey` — the entity id —
 * is what makes a new snippet.
 */
export function useProfileScrollTracking<E>(
  entity: E | undefined,
  resetKey: unknown,
  track: (entity: E, metrics: ProfileScrollMetrics) => void,
): (element: HTMLElement) => void {
  const hasScrolled = useRef(false)
  const latest = useRef({ entity, track })
  latest.current = { entity, track }

  useEffect(() => {
    hasScrolled.current = false
  }, [resetKey])

  return useCallback((element: HTMLElement) => {
    if (hasScrolled.current) return
    hasScrolled.current = true
    setTimeout(() => {
      const { entity: current, track: send } = latest.current
      // `this.candidateAlertInfo.id` on an undefined candidate throws inside the debounce
      // upstream, so nothing is sent; there is no scrollable snippet without one anyway.
      if (current === undefined) return
      send(current, measure(element))
    }, SCROLL_TRACK_DELAY_MS)
  }, [])
}
